/**
 * The one lite-scan submission client (issue #779, slice 1 of the
 * 2026-08-22 ratified decision comment).
 *
 * Before this module, `POST /api/lite-scan` was submitted from two separate
 * call sites, functionally identical (same endpoint, same body shape, both
 * anonymous) but with divergent error handling: the dashboard's
 * `startLiteScan` (packages/frontend/src/utils/api.ts, via `assertOk` — a
 * friendly 429 override, no `retryAfter` surfaced) and the marketing widget's
 * own raw `fetch` (www/src/app/components/LiteScanWidget.tsx — a bare `throw`
 * on any non-ok response, 429 included, plus its own bespoke abort-timeout
 * handling from issue #873). This is that submission, typed once, with one
 * 429 mapping both callers now share.
 *
 * The *authenticated* full-scan submission (`startScan` in api.ts) stays on
 * its own path — different auth, body shape, and SSE-follow wiring make that
 * a real interface decision (per every prior investigation comment on #779),
 * not a mechanical dedupe. It does pick up the same friendly 429 copy, via
 * `classifyQueueBusy` in ./queue-busy, so the message is consistent even
 * though the request itself is not yet unified.
 *
 * "Follow" — watching a submitted scan to a terminal state — is already
 * unified in `watchScan`, above in this same package (issue #740); this
 * module only owns the request that starts one.
 */

import { classifyQueueBusy } from './queue-busy';
import type { CampaignAttribution } from './campaign';
import type { ClickId } from './click-id';
import type { MetaAttribution } from './tracking';

export interface SubmitLiteScanOptions {
  /** API root including `/api`, e.g. `https://privisy.onrender.com/api`. */
  apiBase: string;
  url: string;
  /** GPC-only or opt-out-link-only lite scan; omitted runs the full lite scan. */
  mode?: 'gpc' | 'optout';
  /** Landing-page experiment attribution — sent only when both are present. */
  experimentId?: string;
  variant?: string;
  /**
   * Google Ads lead attribution: the transaction_id the client already sent to
   * gtag, plus the click id from the ad landing URL. Sent only when both are
   * present — one without the other can be neither attributed nor deduped —
   * and both are null for a visitor who opted out or is signalling GPC.
   */
  leadEventId?: string | null;
  clickId?: ClickId | null;
  /**
   * Meta browser attribution (_fbp, _fbc). Sent only when hasConsent() is true;
   * null or omitted when visitor has opted out or signals GPC.
   */
  meta?: MetaAttribution | null;
  /**
   * First-party UTM attribution (./campaign). Not consent-gated: it names the
   * campaign, not the visitor, and is stored only on our own report row.
   */
  campaign?: CampaignAttribution | null;
  /**
   * Abort the request after this many ms (issue #873 — a hung start request
   * used to freeze the widget forever with no way out). Omit to never abort.
   */
  timeoutMs?: number;
  /** Injectable for tests; defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
}

export interface LiteScanRejection {
  /** The backend's prose `error` field, when present; kept for existing callers. Never switch on it. */
  code: string;
  /** The body's typed `code` (#1695), e.g. 'ANON_AUDIT_DAILY_LIMIT', when it sent one (#1992). */
  apiCode?: string;
  /** The resolved, user-facing message — already picks the friendliest available copy. */
  message: string;
  /** 0 when the request never reached the server (network error or abort timeout). */
  status: number;
  /** Only set on a 429 body that carried one. */
  retryAfter?: number;
  /** True when this rejection is the #873 start-request timeout, not a server response. */
  timedOut?: boolean;
}

export type SubmitLiteScanOutcome =
  | {
      accepted: true;
      scanId: string;
      intentToken: string;
      /** Anonymous audit only (#1778): past the daily ceiling it ran as a GPC-only lite scan. */
      fallback?: 'gpc-only';
    }
  | { accepted: false; rejection: LiteScanRejection };

const TIMEOUT_MESSAGE = 'The scanner took too long to respond. Please try again.';
const JSON_HEADERS = { 'Content-Type': 'application/json' };

/** Submit an anonymous lite scan. Never throws — failures come back as a typed rejection. */
export async function submitLiteScan(options: SubmitLiteScanOptions): Promise<SubmitLiteScanOutcome> {
  return submitAnonymous(options, (doFetch, base, body, signal) =>
    doFetch(`${base}/lite-scan`, { method: 'POST', headers: JSON_HEADERS, body, signal }));
}

/**
 * Submit an anonymous full audit, `POST /api/anonymous-audit` (#1777): the
 * full four-section audit, persisted locked and ownerless. Same options
 * (minus `mode`), same outcome and the same rejection mapping as
 * `submitLiteScan`, because the route answers with the lite lane's shapes.
 */
export async function submitAnonymousAudit(
  options: Omit<SubmitLiteScanOptions, 'mode'>,
): Promise<SubmitLiteScanOutcome> {
  return submitAnonymous(options, (doFetch, base, body, signal) =>
    doFetch(`${base}/anonymous-audit`, { method: 'POST', headers: JSON_HEADERS, body, signal }));
}

/**
 * Each lane spells its own `doFetch(\`${base}/<route>\`, { method: 'POST' })`
 * literal so `contract:check` (scripts/api-contract-lib.js) can read the route
 * and fail when the backend renames it (#1992); a computed path hid both.
 */
type SendAnonymous = (
  doFetch: typeof fetch,
  base: string,
  body: string,
  signal: AbortSignal | undefined,
) => Promise<Response>;

async function submitAnonymous(
  options: SubmitLiteScanOptions,
  send: SendAnonymous,
): Promise<SubmitLiteScanOutcome> {
  const { apiBase, url, mode, experimentId, variant, leadEventId, clickId, meta, campaign, timeoutMs, fetchImpl } = options;
  const base = apiBase.replace(/\/$/, '');
  const doFetch: typeof fetch = fetchImpl ?? ((...args) => fetch(...args));

  const abortController = timeoutMs !== undefined ? new AbortController() : undefined;
  // A native AbortSignal.timeout() would be simpler but ignores fake timers in
  // tests (it schedules on Node's internal timer, not the patched global
  // setTimeout) — a plain timer keeps this testable (carried over from #873).
  const timeoutId =
    abortController !== undefined ? setTimeout(() => abortController.abort(), timeoutMs) : undefined;

  let response: Response;
  try {
    response = await send(
      doFetch,
      base,
      JSON.stringify({
        url,
        ...(mode ? { mode } : {}),
        ...(experimentId && variant ? { experimentId, variant } : {}),
        ...(leadEventId && clickId ? { leadEventId, clickId } : {}),
        ...(meta ? { meta } : {}),
        ...(campaign ? { campaign } : {}),
      }),
      abortController?.signal,
    );
  } catch (err) {
    const isTimeout = err instanceof Error && err.name === 'AbortError';
    return {
      accepted: false,
      rejection: {
        code: '',
        message: isTimeout ? TIMEOUT_MESSAGE : err instanceof Error ? err.message : 'Failed to connect to scanner',
        status: 0,
        timedOut: isTimeout,
      },
    };
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const parsed = await response.json().catch(() => ({}));
    const body: Record<string, unknown> = parsed && typeof parsed === 'object' ? parsed : {};
    const code = typeof body.error === 'string' ? body.error : '';
    const apiCode = typeof body.code === 'string' ? body.code : undefined;
    const bodyMessage = typeof body.message === 'string' ? body.message : '';
    // The one shared 429 classifier (./queue-busy) owns both the friendly copy
    // and the retryAfter extraction, so this lane can't drift from the other
    // scan-start lanes that use it directly.
    const queueBusy = classifyQueueBusy(response.status, body);
    // Same priority as api.ts's assertOk: a friendly status override wins,
    // then the backend's error code, then its message, then a fallback.
    const message = queueBusy?.message || code || bodyMessage || 'Failed to start scan';
    // queueBusy is null for the daily-cap 429, which still carries retryAfter (#1992).
    const retryAfter =
      queueBusy?.retryAfter ??
      (response.status === 429 && typeof body.retryAfter === 'number' ? body.retryAfter : undefined);
    return {
      accepted: false,
      rejection: {
        code,
        ...(apiCode !== undefined ? { apiCode } : {}),
        message,
        status: response.status,
        retryAfter,
      },
    };
  }

  let payload: { scanId?: string; intentToken?: string; fallback?: unknown };
  try {
    payload = await response.json();
  } catch {
    // A 2xx with a malformed/truncated body — same "no throw" contract as
    // every other failure path here (issue #873: an uncaught rejection at
    // this call site used to leave the widget spinning forever).
    return { accepted: false, rejection: { code: '', message: 'Failed to start scan', status: response.status } };
  }
  return {
    accepted: true,
    scanId: payload.scanId as string,
    intentToken: payload.intentToken as string,
    ...(payload.fallback === 'gpc-only' ? { fallback: 'gpc-only' as const } : {}),
  };
}
